/**
 * Has this user seen the welcome guide? (BEN-141 page guidance, slice 2.)
 *
 * localStorage, deliberately, and NOT a new profile column. The precedent is
 * `onboardingStep.ts`, which chose the same thing for the wizard cursor and wrote down
 * why: the questions that change what a user should SEE are answerable from the server,
 * but "did they read a blurb" is "a cosmetic cursor rather than financial state, [so]
 * localStorage is proportionate for that, and losing it degrades to showing Welcome
 * again". That degradation is the entire downside here: a reinstall, a new browser, or a
 * cleared profile shows the guide once more.
 *
 * WHAT THIS IS NOT. It is not `onboarding_complete` and must never be conflated with it.
 * That flag gates the whole app via OnboardingGate; overloading it to mean "dismissed a
 * dialog" would let a UI dismissal decide whether a user can reach the app at all.
 *
 * Keyed PER USER. The first version of the mobile equivalent was a single global key, so
 * on a shared browser the second account never saw its own onboarding. `onboarding.ts`
 * here is still global because it only caches a value the server re-supplies within one
 * request; this one has no server copy, so a wrong answer is permanent.
 *
 * `clear` exists for the replay path — "can they see it again?" was one of the three
 * questions asked of tour state, and this is the whole of the answer for the carousel.
 * Nothing calls it yet; a Settings entry point is the obvious home and is not in slice 2.
 */

const KEY_PREFIX = "fomo-fi:welcome-guide-seen";

function key(userId: string): string {
  return `${KEY_PREFIX}:${userId}`;
}

export function hasSeenWelcomeGuide(userId: string): boolean {
  try {
    return localStorage.getItem(key(userId)) === "true";
  } catch {
    // Private mode / storage disabled. Returning `true` (rather than false) is the
    // deliberate choice: if we cannot remember the dismissal, the honest failure is to
    // stay quiet, not to re-open a modal on every single navigation.
    return true;
  }
}

export function markWelcomeGuideSeen(userId: string): void {
  try {
    localStorage.setItem(key(userId), "true");
  } catch {
    /* non-fatal: see hasSeenWelcomeGuide */
  }
}

export function clearWelcomeGuideSeen(userId: string): void {
  try {
    localStorage.removeItem(key(userId));
  } catch {
    /* non-fatal */
  }
}

// The host guard (open only after setup) is shared with mobile (shared/utils/welcomeGuide.ts).
export { welcomeGuideHostOpens } from "@fomofi/shared";
